// DIAGNOSTICS — not participant-facing.
//
// Two things live here, both born of failures that were invisible from inside
// the app:
//
//   * The activity log, which survives a crash. The Android freeze froze the
//     whole tab, so an on-screen panel was unreachable exactly when it
//     mattered; the log is written synchronously to localStorage as things
//     happen. Reproduce, force-quit, reopen, read the last line.
//   * The notification section. "FCM accepted the message" and "the phone
//     showed a notification" are different claims, and for a while nothing
//     could tell them apart. Each control below isolates one link in that
//     chain so a failure can be attributed instead of guessed at.
//
// Reached from the build stamp on the Dashboard, or the #m2c2log URL hash.

import { useEffect, useState } from 'react';
import { readLog, readRunMeta, clearLog } from '../utils/m2c2Log';
import {
  readPushLog,
  clearPushLog,
  sendLocalTestNotification,
  collectPushDiagnostics,
  checkForUpdate,
} from '../utils/pushDiag';

const BUILD_ID = typeof __BUILD_ID__ === 'string' ? __BUILD_ID__ : 'dev';

function Section({ title, children }) {
  return (
    <div className="mb-6">
      <h2 className="font-display text-lg mb-2">{title}</h2>
      {children}
    </div>
  );
}

export default function DiagnosticsScreen({ onClose }) {
  const [lines, setLines] = useState(() => readLog());
  const [meta] = useState(() => readRunMeta());
  const [copied, setCopied] = useState(false);

  const [pushInfo, setPushInfo] = useState(null);
  const [pushEvents, setPushEvents] = useState([]);
  const [localTest, setLocalTest] = useState(null);
  const [updateState, setUpdateState] = useState(null);

  async function refreshPush() {
    setPushInfo(await collectPushDiagnostics());
    setPushEvents(await readPushLog());
  }

  useEffect(() => {
    refreshPush();
  }, []);

  const text = [
    `build: ${BUILD_ID}`,
    `run: ${meta ? JSON.stringify(meta) : '(no run recorded)'}`,
    `push: ${pushInfo ? JSON.stringify(pushInfo) : '(not collected)'}`,
    '',
    '--- push events seen by the service worker ---',
    ...pushEvents.map((e) => `${e.at} ${e.type} ${e.payload ?? ''}`),
    '',
    '--- activity log ---',
    ...lines,
  ].join('\n');

  async function copy() {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (e) {
      // Clipboard access is blocked in some contexts; the text below is
      // selectable either way, so this is a convenience rather than the only
      // path.
      setCopied(false);
    }
  }

  return (
    <div className="screen">
      <div className="flex items-center justify-between mb-3">
        <h1 className="font-display text-xl">Diagnostics</h1>
        <button type="button" onClick={onClose} className="text-dim text-sm underline">
          Close
        </button>
      </div>

      <div className="flex-1 overflow-auto -mx-1 px-1">
        <Section title="Version">
          <p className="text-dim text-sm">Build {BUILD_ID}</p>
          <div className="flex gap-3 mt-2">
            <button
              type="button"
              className="text-amber text-sm underline"
              onClick={async () => {
                setUpdateState('checking');
                const r = await checkForUpdate();
                setUpdateState(r.state);
              }}
            >
              Check for updates
            </button>
          </div>
          {updateState === 'checking' && <p className="text-dim text-xs mt-1">Checking…</p>}
          {updateState === 'up-to-date' && (
            <p className="text-ok text-xs mt-1">You're on the latest published version.</p>
          )}
          {updateState === 'update-found' && (
            <p className="text-amber text-xs mt-1">
              A newer version is downloading. It will apply automatically in a moment.
            </p>
          )}
          {updateState && !['checking', 'up-to-date', 'update-found'].includes(updateState) && (
            <p className="text-dim text-xs mt-1">Couldn't check: {updateState}</p>
          )}
        </Section>

        <Section title="Notifications">
          {/* Ordered as the chain runs: can this device display anything at
              all, is it subscribed, and has anything actually arrived. */}
          <button
            type="button"
            className="btn-secondary mb-3"
            onClick={async () => {
              setLocalTest(await sendLocalTestNotification());
              refreshPush();
            }}
          >
            Show a test notification (no server)
          </button>
          {localTest && (
            <p className={`text-xs mb-3 ${localTest.ok ? 'text-ok' : 'text-stop'}`}>
              {localTest.ok
                ? `Sent locally. ${localTest.reason} If nothing appeared, the phone is blocking notifications for this app — check its notification and battery settings.`
                : localTest.reason}
            </p>
          )}

          {pushInfo && (
            <div className="text-xs text-dim space-y-1 mb-3">
              <p>
                Permission: <span className="text-paper">{pushInfo.permission}</span>
                {' · '}
                {pushInfo.standalone ? 'installed to home screen' : 'running in a browser tab'}
              </p>
              <p>
                Push subscription:{' '}
                <span className={pushInfo.subscription ? 'text-paper' : 'text-stop'}>
                  {pushInfo.subscription ?? 'none — this device cannot receive push'}
                </span>
              </p>
              {pushInfo.registrations.map((r) => (
                <p key={r.scope} className="text-dim/60">
                  sw {r.script} @ {r.scope}
                  {r.subscription ? ' (subscribed)' : ''}
                </p>
              ))}
            </div>
          )}

          <p className="text-xs text-dim mb-1">
            Pushes received by this device ({pushEvents.length}):
          </p>
          <div className="bg-panel rounded-xl2 p-2 text-[10px] leading-snug font-mono text-dim max-h-48 overflow-auto">
            {pushEvents.length === 0 ? (
              <p className="text-dim/60">
                Nothing yet. If a send reports success but nothing appears here, the message never
                reached the phone.
              </p>
            ) : (
              pushEvents
                .slice()
                .reverse()
                .map((e) => (
                  <div key={e.id} className="break-words whitespace-pre-wrap mb-1 select-text">
                    {e.at?.slice(11, 19)} {e.type} {e.payload ?? ''}
                  </div>
                ))
            )}
          </div>
          <div className="flex gap-3 mt-2">
            <button type="button" onClick={refreshPush} className="text-dim text-xs underline">
              Refresh
            </button>
            <button
              type="button"
              onClick={async () => {
                await clearPushLog();
                refreshPush();
              }}
              className="text-dim text-xs underline"
            >
              Clear
            </button>
          </div>
        </Section>

        <Section title="Activity log">
          {meta && (
            <p className="text-dim/70 text-[10px] mb-2 break-words">Last run started {meta.started}</p>
          )}
          <div className="flex gap-3 mb-2">
            <button type="button" onClick={copy} className="text-amber text-sm underline">
              {copied ? 'Copied' : 'Copy everything'}
            </button>
            <button
              type="button"
              onClick={() => {
                clearLog();
                setLines([]);
              }}
              className="text-dim text-sm underline"
            >
              Clear
            </button>
            <button
              type="button"
              onClick={() => setLines(readLog())}
              className="text-dim text-sm underline"
            >
              Refresh
            </button>
          </div>

          {lines.length === 0 ? (
            <p className="text-dim text-sm">
              No log recorded yet. Run a session through the activities, then come back here.
            </p>
          ) : (
            <div className="bg-panel rounded-xl2 p-3 text-[10px] leading-snug font-mono text-dim max-h-64 overflow-auto">
              {lines.map((line, i) => (
                <div key={i} className="break-words whitespace-pre-wrap mb-0.5 select-text">
                  {line}
                </div>
              ))}
            </div>
          )}
        </Section>
      </div>
    </div>
  );
}
